import { useState } from "react";
import { X, Loader2, Phone, MessageSquare } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

/** Uses the EXISTING /inquiries endpoint — no parallel enquiry system. */
export default function EnquireSheet({ reel, onClose }) {
    const { user } = useAuth();
    const p = reel.property || {};
    const [name, setName]     = useState(user?.name || "");
    const [email, setEmail]   = useState(user?.email || "");
    const [phone, setPhone]   = useState(user?.phone || "");
    const [message, setMsg]   = useState(`Hi, I'm interested in "${p.title}". Please share more details.`);
    const [busy, setBusy]     = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!p.id) return;
        setBusy(true);
        try {
            await api.post("/inquiries", {
                property_id: p.id,
                name, email, phone, message,
                source_reel_id: reel.id,
            });
            toast.success("Enquiry sent — the owner will get back soon.");
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send enquiry");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-end" style={{ background: "rgba(0,0,0,0.65)" }}
             onClick={onClose} data-testid="reel-enquire-sheet">
            <form
                onSubmit={submit}
                className="w-full bg-white rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-2">
                    <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>
                        Enquire about this property
                    </div>
                    <button type="button" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ background: "#f4f4f5" }} data-testid="reel-enquire-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="rounded-lg border p-2 mb-3 text-[11.5px]"
                     style={{ borderColor: "rgba(0,38,87,0.08)", color: "#002657" }}>
                    <div className="font-bold truncate">{p.title || "Property"}</div>
                    <div className="opacity-70 truncate">{[p.locality, p.city].filter(Boolean).join(", ")}</div>
                </div>
                <div className="space-y-2">
                    <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name"
                        data-testid="reel-enquire-name"
                        className="w-full h-10 px-3 rounded-lg border text-[12.5px]"
                        style={{ borderColor: "rgba(0,38,87,0.15)" }} />
                    <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                        data-testid="reel-enquire-email"
                        className="w-full h-10 px-3 rounded-lg border text-[12.5px]"
                        style={{ borderColor: "rgba(0,38,87,0.15)" }} />
                    <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (10-digit)"
                        data-testid="reel-enquire-phone"
                        className="w-full h-10 px-3 rounded-lg border text-[12.5px]"
                        style={{ borderColor: "rgba(0,38,87,0.15)" }} />
                    <textarea rows={3} value={message} onChange={(e) => setMsg(e.target.value)}
                        data-testid="reel-enquire-message"
                        className="w-full px-3 py-2 rounded-lg border text-[12.5px]"
                        style={{ borderColor: "rgba(0,38,87,0.15)" }} />
                </div>
                <button
                    type="submit" disabled={busy}
                    data-testid="reel-enquire-submit"
                    className="mt-3 w-full py-3 rounded-xl text-[13px] font-extrabold flex items-center justify-center gap-1"
                    style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                    Send Enquiry
                </button>
                <div className="mt-2 text-[10.5px] text-center" style={{ color: "rgba(0,38,87,0.55)" }}>
                    Enquiries are delivered via GPM's verified enquiry system.
                </div>
            </form>
        </div>
    );
}
